import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { getEffectiveLoadRadarLimit } from '@numeo/types'
import { getEntitlementsForPlan } from '@numeo/types-main-web-app'
import { SubscriptionPlan } from '@numeo/types-core'

import { useAppSelector } from 'hooks'
import type { AgenticHubState } from 'features/agentic-hub/store/agenticHubSlice'
import { Button } from 'components/ds2/Button'
import { useSidebarAnchoredPanel } from './useSidebarAnchoredPanel'

/**
 * Quota usage card shown in the sidebar (only when the sidebar is expanded).
 *
 * Tracks Load Hub searches, Load Radar alerts, and AI Hub searches — all
 * three are children of `findLoadsGroup` (Numeo Spot), gated by the same
 * `FeatureType.Extension` flag as that group. `hasSpotAccess` mirrors that
 * gate so the card doesn't show usage for a section the account can't even
 * see in the nav — a plan-entitlement lookup alone doesn't imply feature
 * access, so this check is necessary in addition to it, not redundant with it.
 *
 * Visible for all non-ULTRA Spot users — falls back to FREE plan defaults
 * when plan or quotaUsage is not yet available (e.g. staging, no subscription).
 * ULTRA users have unlimited quotas (hard_limit: null) so no card is shown.
 *
 * Styled as the "Free Usage" card (`new-agent-numeo-main/src/App.tsx`,
 * commit 6708535): a `warning-soft`-tinted card showing only the aggregate
 * percentage + bar (color escalates accent → warning → danger with
 * utilization) and the upgrade CTA. The per-metric breakdown is hidden by
 * default and revealed on hover, in a panel floating above the card.
 *
 * The breakdown panel is portaled to `document.body` (same pattern as
 * `SidebarGroup`'s collapsed-rail popover / `ProfileFooterItem`'s menu)
 * rather than a plain CSS `absolute` child — this card sits inside the
 * scrolling `<nav>`, which clips an in-flow absolute child's shadow and can
 * cut it off entirely depending on scroll position.
 */
export const SidebarQuotaWidget = ({ hasSpotAccess }: { hasSpotAccess: boolean }) => {
    const quotaUsage = useAppSelector((state) => state.auth.quotaUsage)
    const application = useAppSelector((state) => state.auth.application)
    const aiHubSearchUnlimited = useAppSelector((s: { agenticHub: AgenticHubState }) => s.agenticHub.aiHubSearchUnlimited)
    const navigate = useNavigate()
    const [hovered, setHovered] = useState(false)
    const { coords, triggerRef: cardRef, computeCoords } = useSidebarAnchoredPanel<HTMLDivElement>('above', hovered)

    const handleMouseEnter = () => {
        computeCoords()
        setHovered(true)
    }

    if (!hasSpotAccess) return null

    const plan = application?.subscription?.plan
    // ULTRA has all limits null — no widget needed
    if (plan === SubscriptionPlan.ULTRA) return null
    // Label: FREE reads "Free Usage"; a paid finite tier (PRO — Spot searches stay capped like FREE,
    // only ULTRA unlimits them) reads "Usage", so a paying customer isn't mislabelled as on the Free plan.
    const isFreeTier = !plan || plan === SubscriptionPlan.FREE

    // Fall back to FREE entitlements when plan is unknown (staging, no subscription)
    const planEntitlements = getEntitlementsForPlan(plan ?? SubscriptionPlan.FREE)
    const hubLimit = planEntitlements.loadHubSearch?.hard_limit
    // Effective limit includes earned gamification bonuses (company +5, invites +5 each).
    const radarLimit = getEffectiveLoadRadarLimit(planEntitlements.loadRadar?.hard_limit ?? null, application)
    const aiHubLimit = planEntitlements.aiHubSearch?.hard_limit

    const metrics: { label: string; remaining: number; hardLimit: number; unlimited?: boolean }[] = [
        ...(hubLimit ? [{ label: 'Load Hub searches', remaining: quotaUsage?.loadHubSearch ?? hubLimit, hardLimit: hubLimit }] : []),
        ...(radarLimit ? [{ label: 'Load radar alerts', remaining: quotaUsage?.loadRadar ?? radarLimit, hardLimit: radarLimit }] : []),
        ...(aiHubSearchUnlimited
            ? [{ label: 'AI Hub searches', remaining: 0, hardLimit: 0, unlimited: true }]
            : aiHubLimit
              ? [{ label: 'AI Hub searches', remaining: quotaUsage?.aiHubSearch ?? aiHubLimit, hardLimit: aiHubLimit }]
              : []),
    ]

    if (metrics.length === 0) return null

    // Progress-bar colour escalates with utilisation — a visual signal that
    // intentionally complements (not replaces) the plain muted count text.
    const getBarColor = (pct: number): 'accent' | 'warning' | 'danger' => (pct < 70 ? 'accent' : pct < 90 ? 'warning' : 'danger')
    const barColorClass = { accent: 'bg-accent', warning: 'bg-warning', danger: 'bg-danger' }
    const textColorClass = { accent: 'text-foreground-muted', warning: 'text-warning', danger: 'text-danger' }

    // Headline % is the MOST-exhausted metric, not a summed ratio across heterogeneous quotas —
    // summing (e.g. FREE's 30 Load Hub / 10 Load Radar / 15 AI Hub) let a fully-exhausted Load
    // Radar quota (10/10 used) hide inside a deceptively low aggregate (10/55 = 18%, neutral
    // tone) even though one quota is completely spent. Unlimited metrics (AI Hub on some plans)
    // don't have a meaningful "used / cap" and are excluded from this — there's nothing to warn
    // about for a quota with no ceiling.
    const cappedMetrics = metrics.filter((m) => !m.unlimited)
    const pct =
        cappedMetrics.length > 0
            ? Math.round(Math.max(...cappedMetrics.map((m) => (m.hardLimit > 0 ? (Math.max(0, m.hardLimit - m.remaining) / m.hardLimit) * 100 : 0))))
            : 0
    // Clamp — `remaining` can go negative when usage is recorded after the limit already hit
    // (e.g. a burst that landed before the block kicked in), which would otherwise render a
    // >100% bar/label.
    const clampedPct = Math.min(100, Math.max(0, pct))
    const tone = getBarColor(clampedPct)

    return (
        <div ref={cardRef} className="relative mt-2 shrink-0" onMouseEnter={handleMouseEnter} onMouseLeave={() => setHovered(false)}>
            <div className="flex flex-col gap-3 rounded-2xl bg-warning-soft p-3">
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-foreground">{isFreeTier ? 'Free Usage' : 'Usage'}</span>
                        <span className={`text-xs font-semibold ${textColorClass[tone]}`}>{clampedPct}%</span>
                    </div>
                    <div className="h-1 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/20">
                        <div className={`h-full rounded-full transition-[width] duration-300 ${barColorClass[tone]}`} style={{ width: `${clampedPct}%` }} />
                    </div>
                </div>
                <Button variant="primary" size="sm" style={{ width: '100%' }} onClick={() => navigate('/billing')}>
                    Upgrade plan
                </Button>
            </div>

            {/* Per-metric breakdown — hidden until hover. Portaled + fixed-position so it
                floats cleanly above the sidebar's scrolling nav instead of being clipped
                by it or fighting its stacking context for the shadow. */}
            {hovered &&
                coords &&
                createPortal(
                    <div
                        style={{ position: 'fixed', bottom: coords.bottom, left: coords.left, width: coords.width, zIndex: 50 }}
                        className="flex flex-col gap-1.5 rounded-xl border border-border bg-surface p-3 shadow-overlay"
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                    >
                        {metrics.map((m) => (
                            <div key={m.label} className="flex items-center justify-between text-[11px]">
                                <span className="text-foreground">{m.label}</span>
                                <span className="font-semibold text-foreground-muted">
                                    {m.unlimited ? 'Unlimited' : `${Math.max(0, m.hardLimit - m.remaining)} / ${m.hardLimit}`}
                                </span>
                            </div>
                        ))}
                    </div>,
                    document.body
                )}
        </div>
    )
}
